import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Alert,
  Input,
  message,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  FileAddOutlined,
  PrinterOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { trpc, trpcUntypedClient } from '../lib/api';
import { PageContainer, PageHeader } from '../components/common';
import { getMutationPendingState } from '../utils/trpcMutation';
import { useAuthStore } from '../stores/authStore';
import { calculatePurchaseOrderTaxIncludedSummary } from '../utils/purchaseOrderTaxIncludedSummary';

const { Title, Text } = Typography;
const { TextArea } = Input;

type PdfMutationResult = {
  filename: string;
  contentBase64: string;
  mimeType: string;
};
type DocumentSaveMutationResult = {
  documentId: string;
  documentDisplayId?: string | null;
  fileId: string;
  filename: string;
  documentTypeName: string;
  savedToLabel: string;
};
type ApprovalUserSummary = {
  id: string;
  label: string;
  email?: string | null;
} | null;
type PrintableCompanySeal = {
  assetId: string;
  fileId: string;
  imageUrl: string;
} | null;

interface OrderItem {
  item: {
    id: string;
    quantity: number;
    receivedQuantity: number;
    unitPrice: string;
    amount: string;
    notes?: string | null;
  };
  part: {
    id: string;
    name: string;
    displayId: string;
    partNumber?: string | null;
    unit?: string | null;
  };
}

const formatDate = (value?: string | Date | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('ja-JP');
};

const formatMoney = (value?: string | number | null): string => {
  const numericValue = typeof value === 'number' ? value : Number(value ?? 0);
  return `¥${Number.isFinite(numericValue) ? numericValue.toLocaleString() : '0'}`;
};

type ApprovalStatus = 'not_required' | 'draft' | 'pending' | 'approved' | 'rejected';

const approvalStatusView: Record<ApprovalStatus, { label: string; color: string }> = {
  not_required: { label: '承認不要', color: 'default' },
  draft: { label: '承認依頼前', color: 'default' },
  pending: { label: '承認待ち', color: 'warning' },
  approved: { label: '承認済み', color: 'success' },
  rejected: { label: '差し戻し', color: 'error' },
};

const getApprovalStatus = (order: unknown): ApprovalStatus => {
  const status = (order as { approvalStatus?: string | null })?.approvalStatus;
  return status && status in approvalStatusView ? status as ApprovalStatus : 'not_required';
};

const downloadBase64File = (filename: string, contentBase64: string, mimeType: string) => {
  const binaryString = window.atob(contentBase64);
  const bytes = new Uint8Array(binaryString.length);
  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }
  const blob = new Blob([bytes], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export function PurchaseOrderPdfOutputPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const { user } = useAuthStore();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const [savedDocument, setSavedDocument] = useState<DocumentSaveMutationResult | null>(null);
  const [approvalComment, setApprovalComment] = useState('');
  const [isApprovalActionLoading, setIsApprovalActionLoading] = useState(false);

  const { data, isLoading, refetch } = trpc.purchaseOrders.getById.useQuery(
    { id: id! },
    { enabled: !!id }
  );

  const updateStatusMutation = trpc.purchaseOrders.updateStatus.useMutation({
    onSuccess: async () => {
      messageApi.success('発注済みにしました');
      await refetch();
    },
    onError: (error) => {
      messageApi.error(`発注済みへの変更に失敗しました: ${error.message}`);
    },
  });

  const normalizedItems = useMemo<OrderItem[]>(
    () =>
      (data?.items ?? []).map((entry) => ({
        item: {
          id: entry.item?.id ?? '',
          quantity: entry.item?.quantity ?? 0,
          receivedQuantity: entry.item?.receivedQuantity ?? 0,
          unitPrice: entry.item?.unitPrice ?? '0',
          amount: entry.item?.amount ?? '0',
          notes: entry.item?.notes ?? null,
        },
        part: {
          id: entry.part?.id ?? '',
          name: entry.part?.name ?? '',
          displayId: entry.part?.displayId ?? '',
          partNumber: (entry.part as { partNumber?: string | null } | undefined)?.partNumber ?? null,
          unit: entry.part?.unit ?? null,
        },
      })),
    [data?.items]
  );

  if (isLoading || !data) {
    return (
      <PageContainer>
        <Spin size="large" />
      </PageContainer>
    );
  }

  const { order, supplier } = data;
  const companyName = user?.companyName || '発注元会社';
  const contactName = user?.name || '担当者';
  const contactEmail = user?.email || '-';
  const filename = (data as { pdfFilename: string }).pdfFilename;
  const printableCompanySeal = (data as { printableCompanySeal?: PrintableCompanySeal }).printableCompanySeal ?? null;
  const amountSummary = calculatePurchaseOrderTaxIncludedSummary(order, normalizedItems);
  const subtotal = amountSummary.subtotal;
  const tax = amountSummary.tax;
  const shippingCost = amountSummary.shippingCost;
  const totalAmount = amountSummary.totalAmount;
  const approvalRequired = Boolean((order as { approvalRequired?: boolean | null }).approvalRequired);
  const approvalStatus = getApprovalStatus(order);
  const approvalView = approvalStatusView[approvalStatus];
  const isApprovedOrder = approvalRequired && approvalStatus === 'approved';
  const isFormalPdfBlocked = approvalRequired && approvalStatus !== 'approved';
  const blockedMessage = '承認済みになるまで正式PDFを出力できません';
  const downloadButtonLabel = isApprovedOrder ? '承認済みPDFをダウンロード' : 'PDFをダウンロード';
  const approvalAssignee = (data as { approvalAssignee?: ApprovalUserSummary }).approvalAssignee ?? null;
  const approvedByUser = (data as { approvedByUser?: ApprovalUserSummary }).approvedByUser ?? null;
  const approvalRequestComment =
    (data as { approvalRequestComment?: string | null }).approvalRequestComment ?? null;
  const approvedByLabel = approvedByUser?.label ?? (order as { approvedBy?: string | null }).approvedBy ?? '-';
  const approvedAt = (order as { approvedAt?: string | Date | null }).approvedAt ?? null;
  const approvalDecisionComment =
    approvalStatus === 'approved'
      ? ((order as { approvalComment?: string | null }).approvalComment?.trim() || null)
      : null;
  const canSubmitApproval =
    approvalRequired
    && order.status === 'draft'
    && ['draft', 'rejected'].includes(approvalStatus);

  const handlePrint = () => {
    if (isFormalPdfBlocked) {
      messageApi.warning(blockedMessage);
      return;
    }
    window.open(`/print/purchase-orders/${order.id}`, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = async () => {
    if (isFormalPdfBlocked) {
      messageApi.warning(blockedMessage);
      return;
    }
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const data = await trpcUntypedClient.mutation('purchaseOrders.createPdf', {
        id: order.id,
      }) as PdfMutationResult;
      downloadBase64File(data.filename, data.contentBase64, data.mimeType);
      messageApi.success('PDFを作成しました');
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'PDFの作成に失敗しました';
      messageApi.error(messageText);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSaveDocument = async () => {
    if (isFormalPdfBlocked) {
      messageApi.warning(blockedMessage);
      return;
    }
    if (isSavingDocument || savedDocument) return;
    setIsSavingDocument(true);
    try {
      const result = await trpcUntypedClient.mutation('purchaseOrders.savePdfToDocument', {
        id: order.id,
      }) as DocumentSaveMutationResult;
      setSavedDocument(result);
      messageApi.success('発注書を書類に保存しました');
    } catch (error) {
      const messageText = error instanceof Error ? error.message : '書類保存に失敗しました';
      messageApi.error(messageText);
    } finally {
      setIsSavingDocument(false);
    }
  };

  const handleMarkOrdered = () => {
    updateStatusMutation.mutate({ id: order.id, status: 'ordered' });
  };

  const handleSubmitApproval = async () => {
    const trimmedComment = approvalComment.trim();
    setIsApprovalActionLoading(true);
    try {
      await trpcUntypedClient.mutation('purchaseOrders.submitForApproval', {
        id: order.id,
        comment: trimmedComment || undefined,
      });
      messageApi.success('承認依頼を送りました');
      setApprovalComment('');
      await refetch();
    } catch (error) {
      const messageText = error instanceof Error ? error.message : '承認依頼に失敗しました';
      messageApi.error(messageText);
    } finally {
      setIsApprovalActionLoading(false);
    }
  };

  return (
    <PageContainer>
      {contextHolder}

      <PageHeader
        title="発注書を確認して出力"
        extra={
          <Space wrap>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/app/purchase-orders/${order.id}`)}>
              発注詳細へ戻る
            </Button>
            <Button
              icon={<DownloadOutlined aria-hidden="true" />}
              loading={isDownloading}
              disabled={isFormalPdfBlocked}
              onClick={handleDownload}
            >
              {downloadButtonLabel}
            </Button>
            <Button icon={<PrinterOutlined aria-hidden="true" />} disabled={isFormalPdfBlocked} onClick={handlePrint}>
              印刷
            </Button>
            <Button
              type="primary"
              icon={<ShoppingCartOutlined aria-hidden="true" />}
              loading={getMutationPendingState(updateStatusMutation)}
              disabled={order.status === 'ordered' || isFormalPdfBlocked}
              onClick={handleMarkOrdered}
            >
              発注済みにする
            </Button>
          </Space>
        }
      />

      {approvalRequired && (
        <Alert
          type={isFormalPdfBlocked ? 'warning' : 'success'}
          showIcon
          message={
            <Space wrap>
              <Tag color={approvalView.color}>{approvalView.label}</Tag>
              <Text>{isFormalPdfBlocked ? blockedMessage : '承認済みの正式PDFとして出力できます'}</Text>
            </Space>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} xl={13}>
          <Card
            styles={{ body: { background: '#1f2937', padding: 28, overflowX: 'auto' } }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 780,
                minHeight: 900,
                margin: '0 auto',
                background: '#fff',
                padding: 40,
                color: '#0f172a',
                boxShadow: '0 12px 30px rgba(0, 0, 0, 0.25)',
                boxSizing: 'border-box',
              }}
            >
              <Title level={2} style={{ textAlign: 'center', marginTop: 0 }}>
                発注書
              </Title>
              {approvalRequired && approvalStatus === 'approved' && (
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Space direction="vertical" size={4}>
                    <Space wrap size={8}>
                      <Text>承認者: {approvedByLabel}</Text>
                      <Text>承認日: {formatDate(approvedAt)}</Text>
                    </Space>
                    {approvalDecisionComment && (
                      <Text style={{ whiteSpace: 'pre-wrap' }}>
                        承認コメント: {approvalDecisionComment}
                      </Text>
                    )}
                  </Space>
                </div>
              )}

              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <Card size="small" title="発注先">
                    <Space direction="vertical" size={2}>
                      <Text strong>{supplier.name} 御中</Text>
                      <Text>購買担当者様</Text>
                      <Text>FAX {(supplier as { fax?: string | null }).fax ?? '-'}</Text>
                    </Space>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small" title="発注元">
                    <div
                      style={{
                        minHeight: 72,
                        paddingRight: printableCompanySeal ? 72 : 0,
                        position: 'relative',
                      }}
                    >
                      <Space direction="vertical" size={2}>
                        <Text>{companyName}</Text>
                        <Text>担当 {contactName}</Text>
                        <Text>{contactEmail}</Text>
                      </Space>
                      {printableCompanySeal && (
                        <img
                          alt="社印"
                          src={printableCompanySeal.imageUrl}
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 4,
                            width: 54,
                            height: 54,
                            objectFit: 'contain',
                            opacity: 0.96,
                          }}
                        />
                      )}
                    </div>
                  </Card>
                </Col>
              </Row>

              <Card size="small" style={{ marginBottom: 16 }}>
                <Space wrap size={16}>
                  <Text>管理番号: {order.displayId}</Text>
                  <Text>発注日: {formatDate(order.orderDate)}</Text>
                  <Text>納品予定日: {formatDate(order.expectedDeliveryDate)}</Text>
                </Space>
              </Card>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                <thead>
                  <tr style={{ background: '#0f4c8a', color: '#fff' }}>
                      {['品名', '型式', '数量', '単位', '単価(税込)', '金額(税込)'].map((heading) => (
                      <th key={heading} style={{ textAlign: 'left', padding: 8 }}>
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {normalizedItems.map((entry) => (
                    <tr key={entry.item.id} style={{ borderBottom: '1px solid #d9e2ec' }}>
                      <td style={{ padding: 8 }}>{entry.part.name}</td>
                      <td style={{ padding: 8 }}>{entry.part.partNumber ?? entry.part.displayId}</td>
                      <td style={{ padding: 8 }}>{entry.item.quantity}</td>
                      <td style={{ padding: 8 }}>{entry.part.unit ?? '個'}</td>
                      <td style={{ padding: 8 }}>{formatMoney(entry.item.unitPrice)}</td>
                      <td style={{ padding: 8 }}>{formatMoney(entry.item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Row justify="end">
                <Col span={10}>
                  <Row justify="space-between">
                        <Text>小計(税込)</Text>
                    <Text>{formatMoney(subtotal)}</Text>
                  </Row>
                  <Row justify="space-between">
                    <Text>内消費税</Text>
                    <Text>{formatMoney(tax)}</Text>
                  </Row>
                  <Row justify="space-between">
                        <Text>送料(税込)</Text>
                    <Text>{formatMoney(shippingCost)}</Text>
                  </Row>
                  <Row justify="space-between" style={{ borderTop: '1px solid #b6c2cf', paddingTop: 8 }}>
                    <Text strong>税込合計</Text>
                    <Text strong>{formatMoney(totalAmount)}</Text>
                  </Row>
                </Col>
              </Row>

              <Card size="small" title="備考" style={{ marginTop: 16 }}>
                <Text>{order.notes || '-'}</Text>
              </Card>
            </div>
          </Card>
        </Col>

        <Col xs={24} xl={11}>
          <Card title="出力方法">
            <Row gutter={[10, 10]}>
              <Col xs={24} md={12}>
                <Button
                  block
                  size="large"
                  type="primary"
                  ghost
                  loading={isDownloading}
                  disabled={isFormalPdfBlocked}
                  onClick={handleDownload}
                >
                  {downloadButtonLabel}
                </Button>
              </Col>
              <Col xs={24} md={12}>
                <Button block size="large" disabled={isFormalPdfBlocked} onClick={handlePrint}>
                  PDFを印刷
                </Button>
              </Col>
            </Row>

            <Card size="small" title="外部送付用の連絡先" style={{ marginTop: 12 }}>
              <Space direction="vertical" size={4}>
                <Text>メール: {(supplier as { email?: string | null }).email ?? '-'}</Text>
                <Text>FAX: {(supplier as { fax?: string | null }).fax ?? '-'}</Text>
              </Space>
            </Card>

            {approvalRequired && (
              <Card size="small" title="発注承認" style={{ marginTop: 12 }}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Text>{`承認先: ${approvalAssignee?.label ?? '-'}`}</Text>
                  {approvalRequestComment && (
                    <div
                      style={{
                        padding: 12,
                        border: '1px solid #f0f0f0',
                        borderRadius: 4,
                        background: '#fafafa',
                      }}
                    >
                      <Text strong>承認依頼コメント</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ whiteSpace: 'pre-wrap' }}>{approvalRequestComment}</Text>
                      </div>
                    </div>
                  )}
                  {approvalDecisionComment && (
                    <div
                      style={{
                        padding: 12,
                        border: '1px solid #f0f0f0',
                        borderRadius: 4,
                        background: '#fafafa',
                      }}
                    >
                      <Text strong>承認コメント</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ whiteSpace: 'pre-wrap' }}>{approvalDecisionComment}</Text>
                      </div>
                    </div>
                  )}
                  <TextArea
                    rows={3}
                    value={approvalComment}
                    onChange={(event) => setApprovalComment(event.target.value)}
                    placeholder="承認依頼コメント"
                    disabled={!canSubmitApproval || isApprovalActionLoading}
                  />
                  <Button
                    type="primary"
                    loading={isApprovalActionLoading}
                    disabled={!canSubmitApproval}
                    onClick={() => void handleSubmitApproval()}
                  >
                    承認依頼を送る
                  </Button>
                </Space>
              </Card>
            )}

            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
              <Col xs={24} md={12}>
                <Text type="secondary">ファイル名</Text>
                <Input value={filename} readOnly />
              </Col>
              <Col xs={24} md={12}>
                <Text type="secondary">発注元担当者</Text>
                <Input value={contactName} readOnly />
              </Col>
              <Col span={24}>
                <Text type="secondary">発注先への備考</Text>
                <TextArea rows={3} value={order.notes ?? ''} readOnly />
              </Col>
            </Row>

            <Card size="small" title="書類保存" style={{ marginTop: 12 }}>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Space wrap>
                  <Tag color={savedDocument ? 'success' : 'processing'}>
                    {savedDocument ? '保存済み' : '未保存'}
                  </Tag>
                  <Text>書類カテゴリ: {savedDocument?.documentTypeName ?? '発注書'}</Text>
                </Space>
                <Text type="secondary">保存先: {savedDocument?.savedToLabel ?? `発注 ${order.displayId}`}</Text>
                {savedDocument && (
                  <Text type="secondary">書類ID: {savedDocument.documentDisplayId ?? savedDocument.documentId}</Text>
                )}
                <Button
                  block
                  type="primary"
                  icon={<FileAddOutlined aria-hidden="true" />}
                  loading={isSavingDocument}
                  disabled={isFormalPdfBlocked || Boolean(savedDocument)}
                  onClick={() => void handleSaveDocument()}
                >
                  {savedDocument ? '保存済み' : '書類に保存'}
                </Button>
              </Space>
            </Card>

            <Card size="small" title="出力前チェック" style={{ marginTop: 12 }}>
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                {['PDF生成済み', '書類保存の準備済み'].map((label) => (
                  <Row key={label} justify="space-between">
                    <Col>
                      <Space>
                        <CheckCircleOutlined style={{ color: '#389e0d' }} />
                        <Text>{label}</Text>
                      </Space>
                    </Col>
                    <Col>
                      <Tag color="success">OK</Tag>
                    </Col>
                  </Row>
                ))}
              </Space>
            </Card>
          </Card>
        </Col>
      </Row>
    </PageContainer>
  );
}
